import { FC, useEffect, useState } from 'react';
import { Button, Form, Modal, Radio, message } from 'antd';
import { FloatInput, formatContractValueDecimal } from '../Fields/FloatInput';
import * as Yup from 'yup';
import close from '../../assets/RoundedClose.svg';
import type { SupplierI } from '../../pages/TenderViewPage/types';
import { useUpdateSupplierPricingMutation } from '../../api/tenders';
import { yupSync } from '../../utils';
import { Loader } from '../Loader/Loader';
import './SupplierPriceInformationModal.scss';

export type SupplierPriceBasis = 'per_annum' | 'per_month';

interface MessageApi {
    success: (msg: string) => void;
    error: (msg: string) => void;
}

interface SupplierPriceInformationModalProps {
    tenderId: number;
    openModal: boolean;
    setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
    supplier: SupplierI | null;
    messageApi?: MessageApi;
    setTrigger?: React.Dispatch<React.SetStateAction<boolean>>;
}

const priceInformationToFormValues = (
    pi: SupplierI['payload']['price_information']
): { totalPrice: string; priceBasis: SupplierPriceBasis } | null => {
    if (pi == null || pi.price == null) return null;
    const n = Number(pi.price);
    if (!Number.isFinite(n)) return null;
    const raw = String(pi.priceBasis ?? pi.basis ?? '').toLowerCase();
    const priceBasis: SupplierPriceBasis =
        raw === 'month' || raw === 'per_month' ? 'per_month' : 'per_annum';
    const totalPrice = formatContractValueDecimal(String(n));
    return { totalPrice, priceBasis };
};

const validationSchema = Yup.object().shape({
    totalPrice: Yup.string()
        .trim()
        .required('This is a required field')
        .test('valid-price', 'Enter a valid price', (v) => {
            if (!v) return false;
            const core = v.replace(/,/g, '').replace(/\.$/, '');
            return /^\d+(\.\d{1,2})?$/.test(core);
        }),
    priceBasis: Yup.string()
        .oneOf(['per_annum', 'per_month'], 'This is a required field')
        .required('This is a required field')
});

export const SupplierPriceInformationModal: FC<SupplierPriceInformationModalProps> = ({
    tenderId,
    openModal,
    setOpenModal,
    supplier,
    messageApi,
    setTrigger
}) => {
    const [form] = Form.useForm();
    const [isDirty, setIsDirty] = useState(false);
    const [isConfirmModal, setIsConfirmModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [updateSupplierPricing] = useUpdateSupplierPricingMutation();

    useEffect(() => {
        if (openModal && supplier) {
            form.resetFields();
            setIsDirty(false);
            const initial = priceInformationToFormValues(supplier.payload?.price_information);
            if (initial) {
                form.setFieldsValue(initial);
            }
        }
    }, [openModal, supplier?.id, form, supplier]);

    const notifySuccess = (msg: string) => {
        if (messageApi) {
            messageApi.success(msg);
        } else {
            message.success(msg);
        }
    };

    const notifyError = (msg: string) => {
        if (messageApi) {
            messageApi.error(msg);
        } else {
            message.error(msg);
        }
    };

    const handleSubmit = async () => {
        if (isSaving || !supplier) return;
        let values: { totalPrice: string; priceBasis: SupplierPriceBasis };
        try {
            values = await form.validateFields();
        } catch {
            return;
        }
        setIsSaving(true);
        try {
            const price = Number(values.totalPrice.replace(/,/g, ''));
            if (!Number.isFinite(price)) {
                notifyError('Enter a valid price');
                return;
            }
            const basis = values.priceBasis === 'per_annum' ? 'year' : 'month';
            await updateSupplierPricing({
                tender_id: tenderId,
                supplier_id: supplier.id,
                data: { price, basis }
            }).unwrap();
            notifySuccess('Price information saved successfully.');
            setOpenModal(false);
            setTrigger?.((prev) => !prev);
        } catch (err: unknown) {
            const data = err && typeof err === 'object' && 'data' in err ? (err as { data: unknown }).data : null;
            const msg =
                data && typeof data === 'object' && 'message' in data && (data as { message: unknown }).message != null
                    ? String((data as { message: unknown }).message)
                    : null;
            notifyError(msg ?? 'Could not save price information. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        if (isDirty) {
            setIsConfirmModal(true);
        } else {
            setOpenModal(false);
        }
    };

    return (
        <>
            <Modal
                className="supplier-price-information-modal"
                destroyOnClose
                centered
                open={openModal && !!supplier}
                onOk={handleSubmit}
                onCancel={handleClose}
                closeIcon={<img src={close} alt="" />}
                footer={
                    <div style={{ display: 'flex', gap: 16, width: '100%' }}>
                        <Button block className="cancel" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button block className="supplier-price-information-modal__save" onClick={handleSubmit}>
                            {isSaving ? <Loader /> : 'Save'}
                        </Button>
                    </div>
                }
            >
                <div className="modal">
                    <div className="supplier-price-information-modal__header">
                        <div className="supplier-price-information-modal__title-block">
                            <h2 className="supplier-price-information-modal__heading">Supplier Price information</h2>
                            <p className="supplier-price-information-modal__supplier-name">{supplier?.name}</p>
                        </div>
                    </div>

                    <div className="modal__main main supplier-price-information-modal__body">
                        <p className="supplier-price-information-modal__section-lead">
                            Enter the total price of all relevant service components
                        </p>

                        <Form
                            form={form}
                            layout="vertical"
                            requiredMark={false}
                            onValuesChange={() => setIsDirty(true)}
                            onFinish={handleSubmit}
                        >
                            <div className="supplier-price-information-modal__field-block">
                                <span className="supplier-price-information-modal__label">
                                    Total Price
                                    <span className="supplier-price-information-modal__required"> *</span>
                                </span>
                                <Form.Item
                                    className="input-wrapper"
                                    name="totalPrice"
                                    rules={yupSync('totalPrice', validationSchema, true)}
                                >
                                    <FloatInput
                                        floatLabel="Enter ex VAT Price"
                                        className="input"
                                        type="contractValueDecimal"
                                        maxLength={20}
                                        required
                                        autoComplete="off"
                                        setChanged={setIsDirty}
                                    />
                                </Form.Item>
                            </div>

                            <div className="supplier-price-information-modal__field-block">
                                <span className="supplier-price-information-modal__label">
                                    Price Basis
                                    <span className="supplier-price-information-modal__required"> *</span>
                                </span>
                                <Form.Item name="priceBasis" rules={yupSync('priceBasis', validationSchema, true)}>
                                    <Radio.Group className="supplier-price-information-modal__radio-group">
                                        <Radio className="supplier-price-information-modal__radio" value="per_annum">
                                            Per Annum
                                        </Radio>
                                        <Radio className="supplier-price-information-modal__radio" value="per_month">
                                            Per Month
                                        </Radio>
                                    </Radio.Group>
                                </Form.Item>
                            </div>
                        </Form>
                    </div>
                </div>
            </Modal>

            <Modal
                className="confirmModal"
                centered
                open={isConfirmModal}
                closeIcon={<div style={{ display: 'none' }} />}
                width={600}
                footer={
                    <div style={{ display: 'flex', gap: 16, width: '100%' }}>
                        <Button block className="cancel" onClick={() => setIsConfirmModal(false)}>
                            Cancel
                        </Button>
                        <button
                            type="button"
                            className="quit"
                            onClick={() => {
                                setOpenModal(false);
                                setIsConfirmModal(false);
                            }}
                        >
                            Quit
                        </button>
                    </div>
                }
            >
                <div className="modal-confirm">Your data won’t be saved! Are you sure you want to quit?</div>
            </Modal>
        </>
    );
};
